import { useEffect,useMemo,useState } from "react";
import { Box,Typography,Button,TextField,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,CircularProgress,Dialog,DialogTitle,DialogContent,DialogActions,Switch } from "@mui/material";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const emptyForm={
  name:"",
  active:true,
};

const getErrorMessage=(data,fallback)=>{
  if(data?.error) return data.error;

  if(data&&typeof data==="object"){
    const messages=Object.values(data)
      .flat()
      .filter((value)=>typeof value==="string");

    if(messages.length) return messages.join(" ");
  }

  return fallback;
};

export default function AdminVenues(){
  const {colors,typography}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [venues,setVenues]=useState([]);
  const [search,setSearch]=useState("");
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [modalOpen,setModalOpen]=useState(false);
  const [selectedVenue,setSelectedVenue]=useState(null);
  const [form,setForm]=useState(emptyForm);

  const loadVenues=async()=>{
    try{
      setLoading(true);

      const response=await fetch("/matches/venues/",{
        credentials:"include",
      });

      const data=await response.json();

      if(!response.ok){
        throw new Error(getErrorMessage(data,"No se pudieron cargar las canchas."));
      }

      setVenues(Array.isArray(data)?data:data.results||[]);
    }catch(error){
      showError(error.message);
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadVenues();
  },[]);

  const filteredVenues=useMemo(()=>{
    const value=search.trim().toLowerCase();

    if(!value) return venues;

    return venues.filter((venue)=>
      String(venue.name||"").toLowerCase().includes(value)
    );
  },[venues,search]);

  const handleCreate=()=>{
    setSelectedVenue(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const handleEdit=(venue)=>{
    setSelectedVenue(venue);
    setForm({
      name:venue.name||"",
      active:venue.active!==false,
    });
    setModalOpen(true);
  };

  const handleClose=()=>{
    if(saving) return;

    setModalOpen(false);
    setSelectedVenue(null);
    setForm(emptyForm);
  };

  const handleChange=(event)=>{
    const {name,value}=event.target;

    setForm((prev)=>({
      ...prev,
      [name]:value,
    }));
  };

  const handleSave=async()=>{
    const name=form.name.trim();

    if(!name){
      showError("El nombre de la cancha es obligatorio.");
      return;
    }

    try{
      setSaving(true);

      const payload={
        name,
        active:form.active,
      };

      const response=await fetch(
        selectedVenue
          ? `/matches/venues/${selectedVenue.slug}/`
          : "/matches/venues/",
        {
          method:selectedVenue?"PATCH":"POST",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken"),
          },
          body:JSON.stringify(payload),
        }
      );

      const data=await response.json();

      if(!response.ok){
        throw new Error(getErrorMessage(data,"No se pudo guardar la cancha."));
      }

      const editing=Boolean(selectedVenue);

      setModalOpen(false);
      setSelectedVenue(null);
      setForm(emptyForm);

      await loadVenues();

      showSuccess(
        editing
          ?"Cancha actualizada correctamente."
          :"Cancha creada correctamente."
      );
    }catch(error){
      showError(error.message);
    }finally{
      setSaving(false);
    }
  };

  const handleActiveChange=async(venue)=>{
    try{
      const response=await fetch(`/matches/venues/${venue.slug}/`,{
        method:"PATCH",
        credentials:"include",
        headers:{
          "Content-Type":"application/json",
          "X-CSRFToken":getCookie("csrftoken"),
        },
        body:JSON.stringify({
          active:!venue.active,
        }),
      });

      const data=await response.json();

      if(!response.ok){
        throw new Error(getErrorMessage(data,"No se pudo actualizar el estado de la cancha."));
      }

      await loadVenues();

      showSuccess(
        venue.active
          ?"Cancha desactivada correctamente."
          :"Cancha activada correctamente."
      );
    }catch(error){
      showError(error.message);
    }
  };

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box
        sx={{
          display:"flex",
          flexDirection:{xs:"column",sm:"row"},
          alignItems:{xs:"stretch",sm:"center"},
          justifyContent:"space-between",
          gap:1.5,
          mb:2.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:{xs:typography.sizes.xl,md:typography.sizes.pageTitle},
              fontWeight:typography.weights.extraBold,
              color:colors.text,
            }}
          >
            Canchas
          </Typography>

          <Typography
            sx={{
              mt:0.4,
              fontSize:typography.sizes.md,
              color:colors.textSecondary,
            }}
          >
            Administra las canchas disponibles para la programación de partidos.
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleCreate}
          sx={{
            px:2.2,
            backgroundColor:colors.secondary,
            "&:hover":{
              backgroundColor:colors.secondaryHover,
            },
          }}
        >
          Nueva cancha
        </Button>
      </Box>

      <Box
        sx={{
          mb:1.5,
          display:"flex",
          justifyContent:"flex-end",
        }}
      >
        <TextField
          value={search}
          onChange={(event)=>setSearch(event.target.value)}
          placeholder="Buscar cancha"
          sx={{
            width:{xs:"100%",sm:320},
            "& .MuiOutlinedInput-root":{
              backgroundColor:colors.surface,
            },
          }}
        />
      </Box>

      <TableContainer
        sx={{
          border:`1px solid ${colors.border}`,
          borderRadius:"10px",
          backgroundColor:colors.surface,
          overflowX:"auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor:colors.tableHeader,
              }}
            >
              <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>
                Cancha
              </TableCell>

              <TableCell align="center" sx={{color:colors.tableHeaderText,fontWeight:700}}>
                Activa
              </TableCell>

              <TableCell align="right" sx={{color:colors.tableHeaderText,fontWeight:700}}>
                Acción
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading?(
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{py:5}}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ):filteredVenues.length===0?(
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{
                    py:5,
                    color:colors.textSecondary,
                  }}
                >
                  No hay canchas registradas.
                </TableCell>
              </TableRow>
            ):(
              filteredVenues.map((venue)=>(
                <TableRow
                  key={venue.slug}
                  hover
                  sx={{
                    "&:last-child td":{
                      borderBottom:0,
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize:typography.sizes.md,
                        fontWeight:600,
                        color:colors.text,
                      }}
                    >
                      {venue.name}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={venue.active!==false}
                      onChange={()=>handleActiveChange(venue)}
                    />
                  </TableCell>

                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={()=>handleEdit(venue)}
                      sx={{
                        minWidth:0,
                        px:1.5,
                        color:colors.secondary,
                      }}
                    >
                      Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={modalOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx:{
            borderRadius:"12px",
            backgroundColor:colors.surface,
            backgroundImage:"none",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb:1,
            fontSize:typography.sizes.xl,
            fontWeight:700,
            color:colors.text,
          }}
        >
          {selectedVenue?"Editar cancha":"Nueva cancha"}
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              pt:1,
              display:"flex",
              flexDirection:"column",
              gap:1.5,
            }}
          >
            <TextField
              name="name"
              label="Nombre"
              value={form.name}
              onChange={handleChange}
              fullWidth
              required
            />

            {selectedVenue&&(
              <Box
                sx={{
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"space-between",
                  border:`1px solid ${colors.border}`,
                  borderRadius:"8px",
                  px:1.5,
                  py:0.7,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize:typography.sizes.md,
                      fontWeight:600,
                      color:colors.text,
                    }}
                  >
                    Cancha activa
                  </Typography>

                  <Typography
                    sx={{
                      mt:0.2,
                      fontSize:typography.sizes.sm,
                      color:colors.textSecondary,
                    }}
                  >
                    Las canchas inactivas no deben utilizarse para nuevas programaciones.
                  </Typography>
                </Box>

                <Switch
                  checked={form.active}
                  onChange={(event)=>setForm((prev)=>({...prev,active:event.target.checked}))}
                />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{px:3,pb:2.5,pt:1}}>
          <Button
            onClick={handleClose}
            disabled={saving}
            sx={{
              color:colors.textSecondary,
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              px:2.2,
              backgroundColor:colors.secondary,
              "&:hover":{
                backgroundColor:colors.secondaryHover,
              },
            }}
          >
            {saving?"Guardando...":"Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}